import { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { FieldRow } from "@/components/ui/Field";
import { formatCurrency } from "@/lib/documentCalc";
import {
  DOKUMENT_STATUS_LABELS,
  dokumentDisplayNumber,
  formatBelegdatum,
  listBeauftragbareAngebote,
  type BeauftragbaresAngebot,
} from "@/lib/dokumente";
import { useFloatingPanel, estimateAutocompletePanelHeight, floatingPanelStyle } from "@/lib/useFloatingPanel";

function formatAngebotLabel(angebot: BeauftragbaresAngebot): string {
  const nummer = dokumentDisplayNumber(angebot) || `Angebot #${angebot.id}`;
  const parts = [nummer, angebot.auftrag_nummer, angebot.kunde_name, angebot.betreff].filter(Boolean);
  return parts.join(" · ");
}

export function AngebotBeauftragungPicker({
  value,
  onChange,
  auftragId,
  disabled = false,
  listboxZIndexClass = "z-[900]",
}: {
  value: BeauftragbaresAngebot | null;
  onChange: (next: BeauftragbaresAngebot | null) => void;
  auftragId?: number | null;
  disabled?: boolean;
  listboxZIndexClass?: string;
}) {
  const id = useId();
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<BeauftragbaresAngebot[]>([]);
  const [loading, setLoading] = useState(false);
  const closeTimer = useRef<number | null>(null);

  const panelHeightEstimate = useMemo(
    () =>
      estimateAutocompletePanelHeight({
        optionCount: options.length,
        loading,
        empty: !loading && options.length === 0,
        hasDescription: options.some((a) => Boolean(a.betreff || a.auftrag_beschreibung)),
        maxHeightCap: 320,
      }),
    [loading, options],
  );
  const floating = useFloatingPanel(triggerRef, open, {
    gap: 4,
    maxHeightCap: 320,
    minBelow: 180,
    contentHeightEstimate: panelHeightEstimate,
  });

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const list = await listBeauftragbareAngebote({
          auftragId: auftragId ?? undefined,
          q: query.trim() || undefined,
          limit: 20,
        });
        if (!cancelled) {
          setOptions(Array.isArray(list) ? list.filter((item) => item.id !== value?.id) : []);
        }
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [auftragId, open, query, value?.id]);

  function select(angebot: BeauftragbaresAngebot) {
    onChange(angebot);
    setQuery("");
    setOpen(false);
  }

  function clear() {
    onChange(null);
    setQuery("");
  }

  function scheduleClose() {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(false), 120);
  }

  return (
    <FieldRow
      label="Angebot als Beauftragung (optional)"
      hint="Suche nach Angebotsnummer, Betreff, Projekt oder Kunde. Das Zielprojekt wird automatisch aus dem Angebot übernommen."
      fieldId={id}
    >
      <div className="relative">
        <div
          ref={triggerRef}
          className={clsx(
            "flex min-h-[42px] w-full flex-wrap items-center gap-1.5 rounded-lg border border-separator bg-card px-2 py-1 shadow-sm focus-within:border-accent focus-within:ring-2 focus-within:ring-accent-soft",
            disabled && "opacity-60",
          )}
          onClick={() => {
            if (disabled) return;
            setOpen(true);
            inputRef.current?.focus();
          }}
        >
          {value ? (
            <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-accent-soft px-2 py-0.5 text-xs font-medium text-accent">
              <span className="truncate">{formatAngebotLabel(value)}</span>
              <button
                type="button"
                aria-label="Angebot entfernen"
                className="shrink-0 rounded-full p-0.5 text-accent hover:bg-fill-secondary"
                disabled={disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  clear();
                }}
              >
                ×
              </button>
            </span>
          ) : null}
          <input
            ref={inputRef}
            id={id}
            type="text"
            disabled={disabled}
            className="min-w-[160px] flex-1 border-0 bg-transparent p-1 text-sm text-label placeholder:text-label-tertiary focus:outline-none focus:ring-0"
            placeholder={value ? "" : "Erstellte Angebote suchen…"}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onBlur={scheduleClose}
            onKeyDown={(e) => {
              if (e.key === "Enter" && options[0]) {
                e.preventDefault();
                select(options[0]);
              }
              if (e.key === "Escape") setOpen(false);
            }}
          />
        </div>
        {open && floating && typeof document !== "undefined"
          ? createPortal(
              <ul
                id={`${id}-listbox`}
                role="listbox"
                className={clsx(
                  listboxZIndexClass,
                  "overflow-auto rounded-lg border border-separator bg-card py-1 text-sm shadow-ios-md",
                )}
                style={floatingPanelStyle(floating)}
                onMouseDown={(e) => e.preventDefault()}
              >
                {loading && <li className="px-3 py-2 text-label-tertiary">Suche …</li>}
                {!loading && options.length === 0 && (
                  <li className="px-3 py-2 text-label-tertiary">Keine beauftragbaren Angebote …</li>
                )}
                {!loading &&
                  options.map((angebot) => (
                    <li key={angebot.id} role="option">
                      <button
                        type="button"
                        className="block w-full px-3 py-2 text-left text-label hover:bg-fill-tertiary"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          select(angebot);
                        }}
                      >
                        <div className="font-medium">{formatAngebotLabel(angebot)}</div>
                        <div className="text-xs text-label-secondary">
                          {DOKUMENT_STATUS_LABELS[angebot.status]}
                          {angebot.belegdatum ? ` · ${formatBelegdatum(angebot.belegdatum)}` : ""}
                          {` · ${formatCurrency(angebot.summe_brutto, angebot.waehrung)}`}
                        </div>
                        {angebot.auftrag_beschreibung ? (
                          <div className="text-xs text-label-tertiary">{angebot.auftrag_beschreibung}</div>
                        ) : null}
                      </button>
                    </li>
                  ))}
              </ul>,
              document.body,
            )
          : null}
      </div>
    </FieldRow>
  );
}
